// AIConsultantProvider — controle global do drawer do Consultor IA.
//
// Um único drawer no root; qualquer botão em qualquer lugar chama open().

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { AIConsultantDrawer } from "./AIConsultantDrawer";
import type { BlockKey } from "@/lib/business-blocks";

export type AIConsultantOpenArgs = {
  sectionKey?: BlockKey | string | null;
  fieldKey?: string | null;
  conversationId?: string | null;
  intent?: "conversation" | "field_suggestion" | "review";
};

type Ctx = {
  open: (args?: AIConsultantOpenArgs) => void;
  close: () => void;
};

const AIConsultantContext = createContext<Ctx | null>(null);

export function AIConsultantProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ open: boolean; args: AIConsultantOpenArgs }>({
    open: false,
    args: {},
  });

  const open = useCallback((args: AIConsultantOpenArgs = {}) => {
    setState({ open: true, args });
  }, []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <AIConsultantContext.Provider value={value}>
      {children}
      <AIConsultantDrawer
        isOpen={state.open}
        onClose={close}
        sectionKey={(state.args.sectionKey ?? null) as BlockKey | null}
        fieldKey={state.args.fieldKey ?? null}
        conversationId={state.args.conversationId ?? null}
        intent={state.args.intent}
      />
    </AIConsultantContext.Provider>
  );
}

export function useAIConsultant() {
  const ctx = useContext(AIConsultantContext);
  if (!ctx) {
    // silencioso: botão só faz nada se provider não montado (por ex. rotas públicas)
    return { open: () => {}, close: () => {} };
  }
  return ctx;
}
